import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useStressEntryContext } from "./StressEntryContext";
import { STRESSOR_CHECK_IN_MODE, STRESSOR_CHECK_OUT_MODE } from "./constants";
import { useHailTestCalculation } from "./stressEntryQueries";
import { Moment } from "moment";
import { DateTimePicker } from "@material-ui/pickers";

import { useFetchContext } from "../../../common/FetchContext";
import { toast } from "react-toastify";
import { useHailFilePost } from "../../../common/services/fileServices";

interface RowData {
  col1: number | "";
  col2: number | "";
  col3: number | "";
  col4: number | "";
  col5: number | "";
}

const createEmptyRow = (): RowData => ({
  col1: "",
  col2: "",
  col3: "",
  col4: "",
  col5: "",
});

export const HailFields = ({ serialnumber, UnitVersion }: any) => {
  const getRowCount = (version: string) => {
    return version !== "v12" ? 11 : 6;
  };

  const [rows, setRows] = useState<RowData[]>(
    Array.from({ length: getRowCount(UnitVersion) }, createEmptyRow),
  );

  useEffect(() => {
    const rowCount = getRowCount(UnitVersion);
    setRows(Array.from({ length: rowCount }, createEmptyRow));
  }, [UnitVersion]);

  const { state, dispatch } = useStressEntryContext();

  const isV12HailTTF =
    UnitVersion === "v12" &&
    state.procedureResultIds?.some(
      (p: any) => p.procedureDefinitionName === "Hail Strike Event v12",
    );
  const { authAxios } = useFetchContext();

  const { data: hailTestCalculation, isLoading: isLoadingHail } =
    useHailTestCalculation(
      serialnumber,
      !!state.isHailTestCheck && state.mode === STRESSOR_CHECK_OUT_MODE,
    );

  const { mutateAsync: uploadHailFile, isLoading } = useHailFilePost();
  const [uploadedData, setUploadedData] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    Ambient_temperature: "",
    Ambient_humidity: "",
    Calibration_date: null as Moment | null,
    Hail_test_specs: null,
    Mounting_method: "",
    Mounting_tilt: UnitVersion === "v12" ? "0" : null,
  });

  const Hail_test_specs = [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75];

  const mountingMethodOptions = [
    "Corner clamps",
    "Tracker 400mm holes",
    "Tracker 790mm holes",
    "Tracker 400mm clamps",
    "Fixed rack ¼ clamps",
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Moment | null) => {
    setFormData((prev) => ({ ...prev, Calibration_date: date }));
  };

  useEffect(() => {
    if (UnitVersion === "v12") {
      setFormData((prev) => ({
        ...prev,
        Mounting_tilt: prev.Mounting_tilt || "0",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        Mounting_tilt: "",
      }));
    }
  }, [UnitVersion]);

  const calculateImpactEnergy = (massG: any, speed: any) => {
    const m = parseFloat(massG);
    const v = parseFloat(speed);
    if (!isNaN(m) && !isNaN(v)) {
      const massKg = m / 1000;
      return 0.5 * massKg * v * v;
    }
    return "";
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      const procedureResultIds =
        state.procedureResultIds?.map((item) => item.procedureResultId) || [];

      const query = `[${procedureResultIds.join(",")}]`;

      const response = await uploadHailFile({
        authAxios,
        selectedFile: file,
        procedure_result_ids: query,
      });

      // Handle cases where the request succeeds with 200 OK but has a 'status: false' payload flag
      if (response?.status === false) {
        toast.error(response.message || "Validation failed.");
        response.errors?.forEach((item: any) => {
          toast.error(`Row ${item.row}: ${item.message}`);
        });
        setUploadedData([]);
        return;
      }

      const uploadedSerialNumber = response?.results?.[0]?.serial_number;
      if (
        uploadedSerialNumber &&
        String(uploadedSerialNumber).trim() !== String(serialnumber).trim()
      ) {
        toast.error(
          `Serial number mismatch. Expected ${serialnumber}, found ${uploadedSerialNumber}`,
        );
        setUploadedData([]);
        return;
      }

      toast.success("File uploaded successfully.");
      setUploadedData(response.results);
    } catch (errorData: any) {
      console.error("Intercepted Upload Error:", errorData);

      // 1. If we have row-by-row server validation errors, print them and return immediately
      if (errorData?.errors?.length) {
        if (errorData.message) {
          toast.error(errorData.message); // "Validation failed."
        }

        errorData.errors.forEach((err: any, index: number) => {
          setTimeout(() => {
            toast.error(`Row ${err.row}: ${err.message}`);
          }, index * 250);
        });
        return; // Stop execution here
      }
      if (errorData?.error) {
        toast.error(errorData.error);

        if (errorData.missing_columns) {
          toast.error(`Missing columns: ${errorData.missing_columns}`);
        }

        return;
      }

      // 2. If it's a generic server message without individual row errors, show it and exit
      if (errorData?.message) {
        toast.error(errorData.message);
        return; // Stop execution here
      }

      // 3. Fallbacks if the error is completely unstyled or raw text
      if (typeof errorData === "string") {
        toast.error(errorData);
      } else {
        toast.error("File upload failed.");
      }
    }
  };

  const validateUploadedRow = (row: any) => {
    const result = hailTestCalculation?.results;

    if (!result) {
      return {};
    }

    const hailSpec = result.hail_spec || 0;

    const targetIndex = result.diameterOptions?.findIndex(
      ([min, max]: [number, number]) => hailSpec >= min && hailSpec <= max,
    );

    if (targetIndex === -1) {
      return {};
    }

    const diameterRange = result.diameterOptions[targetIndex];
    const massRange = result.massOptions[targetIndex];
    const velocityRange = result.velocityOptions[targetIndex];

    return {
      diameterMismatch:
        row.iceball_diameter < diameterRange[0] ||
        row.iceball_diameter > diameterRange[1],

      massMismatch:
        row.iceball_mass < massRange[0] || row.iceball_mass > massRange[1],

      velocityMismatch:
        row.iceball_speed < velocityRange[0] ||
        row.iceball_speed > velocityRange[1],

      impactMismatch:
        row.impact_energy < result.impactenergy_min ||
        row.impact_energy > result.impactenergy_max,

      diameterRange,
      massRange,
      velocityRange,
      impactRange: [result.impactenergy_min, result.impactenergy_max],
    };
  };

  const handleInputChange = (
    rowIndex: number,
    field: keyof RowData,
    value: number,
  ) => {
    const updatedRows = [...rows];
    updatedRows[rowIndex][field] = value;

    // Auto-update impact energy when mass or velocity changes
    if (field === "col3" || field === "col4") {
      const mass = updatedRows[rowIndex].col3;
      const velocity = updatedRows[rowIndex].col4;
      const impactEnergy = calculateImpactEnergy(mass, velocity);
      updatedRows[rowIndex].col5 = parseFloat(impactEnergy as string) || 0;
    }

    setRows(updatedRows);
  };

  useEffect(() => {
    if (
      isV12HailTTF &&
      uploadedData.length > 0 &&
      state.mode === STRESSOR_CHECK_OUT_MODE
    ) {
      const payload = {
        entries: uploadedData?.[0]?.hail_test_data?.map((item: any) => ({
          shot_position: Number(item.shot_position),
          iceball_diameter: item.iceball_diameter,
          iceball_mass: item.iceball_mass,
          iceball_speed: item.iceball_speed,
          impact_energy: item.impact_energy,
          result: item.result,
        })),
      };

      console.log("V12 Payload:", payload);

      dispatch({
        type: "HAILTEST_STRESS_EXIT_DETAILS",
        payload,
      });
    }
  }, [uploadedData, UnitVersion, state.mode, dispatch]);

  useEffect(() => {
    if (state.mode === STRESSOR_CHECK_IN_MODE && state.isHailTestCheck) {
      const payload = {
        Calibration_date: formData.Calibration_date
          ? formData.Calibration_date.format("YYYY-MM-DD")
          : null,
        Ambient_temperature: formData.Ambient_temperature
          ? parseFloat(formData.Ambient_temperature)
          : null,
        Ambient_humidity: formData.Ambient_humidity
          ? parseFloat(formData.Ambient_humidity)
          : null,
        Hail_test_specs: formData.Hail_test_specs,
        Mounting_method: formData.Mounting_method,
        Mounting_tilt: formData.Mounting_tilt
          ? parseFloat(formData.Mounting_tilt)
          : null,
      };
      console.log("Auto-updated payload:", payload);
      dispatch({ type: "HAILTEST_STRESS_DETAILS", payload });
    }
  }, [formData, dispatch, state.mode, state.isHailTestCheck]);

  useEffect(() => {
    const filteredEntries = rows
      .map((row) => {
        const filteredRow = Object.fromEntries(
          Object.entries({
            shot_position: row.col1,
            iceball_diameter: row.col2,
            iceball_mass: row.col3,
            iceball_speed: row.col4,
            impact_energy: row.col5,
          }).filter(
            ([_, v]) =>
              v !== "" && v !== null && v !== undefined && !Number.isNaN(v),
          ),
        );
        return filteredRow;
      })
      .filter((row) => Object.keys(row).length > 0);

    const payload = { entries: filteredEntries };
    console.log("Payload entries:", payload);
    dispatch({ type: "HAILTEST_STRESS_EXIT_DETAILS", payload });
  }, [rows, dispatch]);

  const handleAddRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  return (
    <div>
      {state.mode === STRESSOR_CHECK_IN_MODE && state.isHailTestCheck && (
        <Paper elevation={3} style={{ padding: 10, marginBottom: 16 }}>
          <Grid
            container
            direction="row"
            justifyContent="space-around"
            alignItems="flex-start"
            spacing={2}
            style={{ marginTop: 16, marginBottom: 16 }}
          >
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="Ambient_temperature"
                    label="Ambient temperature"
                    type="number"
                    value={formData.Ambient_temperature}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°C</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Ambient_humidity"
                    label="Ambient humidity"
                    type="number"
                    value={formData.Ambient_humidity}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%RH</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DateTimePicker
                    label="Calibration Date"
                    format="YYYY-MM-DD HH:mm"
                    fullWidth
                    value={formData.Calibration_date}
                    onChange={handleDateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="Hail_test_specs"
                    label="Hail test specs"
                    value={formData.Hail_test_specs}
                    onChange={handleChange}
                    fullWidth
                    required
                  >
                    {Hail_test_specs.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option} mm
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="Mounting_method"
                    label="Mounting method"
                    value={formData.Mounting_method}
                    onChange={handleChange}
                    fullWidth
                    required
                  >
                    {mountingMethodOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Mounting_tilt"
                    label="Mounting tilt"
                    value={formData.Mounting_tilt}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°</InputAdornment>
                      ),
                    }}
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      {state.mode === STRESSOR_CHECK_OUT_MODE &&
        state.isHailTestCheck &&
        !isLoadingHail && (
          <>
            {isV12HailTTF ? (
              <>
                <Paper elevation={3} style={{ padding: 16 }}>
                  <input
                    accept=".xlsx ,.xls,.csv,.txt"
                    style={{ display: "none" }}
                    id="hail-v12-upload"
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploadedData.length > 0}
                  />

                  <label htmlFor="hail-v12-upload">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      disabled={isLoading}
                    >
                      {isLoading ? "Uploading..." : "Upload Hail Test Report"}
                    </Button>
                  </label>
                </Paper>
                {isV12HailTTF && uploadedData.length > 0 && (
                  <TableContainer component={Paper} style={{ marginTop: 16 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Shot Position</TableCell>
                          <TableCell align="center">Diameter (mm)</TableCell>
                          <TableCell align="center">Mass (g)</TableCell>
                          <TableCell align="center">Velocity (m/s)</TableCell>
                          <TableCell align="center">
                            Impact Energy (J)
                          </TableCell>
                          <TableCell align="center">Result</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {uploadedData?.[0]?.hail_test_data?.map(
                          (row: any, index: any) => {
                            const validation = validateUploadedRow(row);

                            return (
                              <TableRow key={index}>
                                <TableCell align="center">
                                  {row.shot_position}
                                </TableCell>

                                <TableCell align="center">
                                  <div
                                    style={{
                                      color: validation.diameterMismatch
                                        ? "red"
                                        : "inherit",
                                    }}
                                  >
                                    {row.iceball_diameter}
                                  </div>

                                  {validation.diameterMismatch && (
                                    <div
                                      style={{
                                        color: "red",
                                        fontSize: "10px",
                                        marginTop: 4,
                                      }}
                                    >
                                      Exp: {validation.diameterRange?.[0]} -{" "}
                                      {validation.diameterRange?.[1]}
                                    </div>
                                  )}
                                </TableCell>

                                <TableCell align="center">
                                  <div
                                    style={{
                                      color: validation.massMismatch
                                        ? "red"
                                        : "inherit",
                                    }}
                                  >
                                    {row.iceball_mass}
                                  </div>

                                  {validation.massMismatch && (
                                    <div
                                      style={{
                                        color: "red",
                                        fontSize: "9px",
                                        marginTop: 4,
                                      }}
                                    >
                                      Exp: {validation.massRange?.[0]} -{" "}
                                      {validation.massRange?.[1]}
                                    </div>
                                  )}
                                </TableCell>

                                <TableCell align="center">
                                  <div
                                    style={{
                                      color: validation.velocityMismatch
                                        ? "red"
                                        : "inherit",
                                    }}
                                  >
                                    {row.iceball_speed}
                                  </div>

                                  {validation.velocityMismatch && (
                                    <div
                                      style={{
                                        color: "red",
                                        fontSize: "9px",
                                        marginTop: 4,
                                      }}
                                    >
                                      Exp: {validation.velocityRange?.[0]} -{" "}
                                      {validation.velocityRange?.[1]}
                                    </div>
                                  )}
                                </TableCell>

                                <TableCell
                                  align="center"
                                  style={{
                                    color: validation.impactMismatch
                                      ? "red"
                                      : "inherit",
                                  }}
                                >
                                  {Number(row.impact_energy).toFixed(2)}
                                </TableCell>

                                <TableCell align="center">
                                  <div>{row.result}</div>
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Shot Position</TableCell>
                      <TableCell align="center">Diameter (mm)</TableCell>
                      <TableCell align="center">Mass (g)</TableCell>
                      <TableCell align="center">Velocity (m/s)</TableCell>
                      <TableCell align="center">Impact Energy (J)</TableCell>
                      <TableCell align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {(
                          [
                            "col1",
                            "col2",
                            "col3",
                            "col4",
                            "col5",
                          ] as (keyof RowData)[]
                        ).map((key) => {
                          const userValue = row[key];
                          const result = hailTestCalculation?.results;
                          const hail_test_entry = result?.hail_spec || 0;

                          const diameterOptions: [number, number][] =
                            result?.diameterOptions ?? [];
                          const massOptions: [number, number][] =
                            result?.massOptions ?? [];
                          const velocityOptions: [number, number][] =
                            result?.velocityOptions ?? [];

                          let mismatch = false;
                          let expectedRange = "";

                          const userVal =
                            typeof userValue === "number"
                              ? userValue
                              : parseFloat(userValue as string) || 0;

                          if (result) {
                            switch (key) {
                              case "col2": {
                                const targetRange = diameterOptions.find(
                                  ([min, max]) =>
                                    hail_test_entry >= min &&
                                    hail_test_entry <= max,
                                );
                                if (hail_test_entry && targetRange) {
                                  const [expectedMin, expectedMax] =
                                    targetRange;
                                  if (
                                    userVal < expectedMin ||
                                    userVal > expectedMax
                                  ) {
                                    mismatch = true;
                                    expectedRange = `${expectedMin} - ${expectedMax}`;
                                  }
                                } else {
                                  mismatch = true;
                                  expectedRange = "Invalid spec";
                                }
                                break;
                              }
                              case "col3": {
                                const targetIndex = diameterOptions.findIndex(
                                  ([min, max]) =>
                                    hail_test_entry >= min &&
                                    hail_test_entry <= max,
                                );
                                if (
                                  targetIndex !== -1 &&
                                  massOptions[targetIndex]
                                ) {
                                  const [min, max] = massOptions[targetIndex];
                                  if (userVal < min || userVal > max) {
                                    mismatch = true;
                                    expectedRange = `${min} - ${max}`;
                                  }
                                } else {
                                  mismatch = true;
                                  expectedRange = "No range defined";
                                }
                                break;
                              }
                              case "col4": {
                                const targetIndex = diameterOptions.findIndex(
                                  ([min, max]) =>
                                    hail_test_entry >= min &&
                                    hail_test_entry <= max,
                                );
                                if (
                                  targetIndex !== -1 &&
                                  velocityOptions[targetIndex]
                                ) {
                                  const [min, max] =
                                    velocityOptions[targetIndex];
                                  if (userVal < min || userVal > max) {
                                    mismatch = true;
                                    expectedRange = `${min} - ${max}`;
                                  }
                                } else {
                                  mismatch = true;
                                  expectedRange = "No range defined";
                                }
                                break;
                              }
                              case "col5": {
                                const min = result.impactenergy_min;
                                const max = result.impactenergy_max;
                                if (
                                  min != null &&
                                  max != null &&
                                  (userVal < min || userVal > max)
                                ) {
                                  mismatch = true;
                                  expectedRange = `${min} - ${max}`;
                                }
                                break;
                              }
                              default:
                                break;
                            }
                          }

                          return (
                            <TableCell key={key} align="center">
                              <TextField
                                type="number"
                                inputProps={{
                                  min: 0,
                                  step: "any",
                                }}
                                value={
                                  key === "col5" &&
                                  typeof userValue === "number"
                                    ? userValue.toFixed(2)
                                    : userValue === 0 || userValue === ""
                                      ? ""
                                      : userValue
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    rowIndex,
                                    key,
                                    e.target.value === ""
                                      ? 0
                                      : parseFloat(e.target.value),
                                  )
                                }
                                variant="filled"
                                size="small"
                                fullWidth
                                style={{ minWidth: 180 }}
                                InputProps={{
                                  style: {
                                    padding: 8,
                                    color: mismatch ? "red" : "inherit",
                                  },
                                }}
                                helperText={
                                  userValue !== "" &&
                                  userValue !== 0 &&
                                  mismatch
                                    ? `Exp: ${expectedRange}`
                                    : "\u00A0"
                                }
                                FormHelperTextProps={{
                                  style: {
                                    color: mismatch ? "red" : "inherit",
                                    fontSize: "0.75rem",
                                    minHeight: "1em",
                                  },
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          {/* 
                   Check 1: Is it NOT v12? 
                   Check 2: Is it the last row? 
                */}
                          {!isV12HailTTF &&
                          rowIndex === rows.length - 1 ? (
                            <IconButton
                              color="primary"
                              onClick={() =>
                                setRows([...rows, createEmptyRow()])
                              }
                              size="small"
                            >
                              <AddIcon />
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
    </div>
  );
};
